import { supabase } from '@/integrations/supabase/client';

const LICENSE_STORAGE_KEY = 'lyra-license-data';

export interface LicenseValidationResult {
  valid: boolean;
  customerName?: string;
  customerEmail?: string;
  licenseKey?: string;
  courseId?: string;
  error?: string;
  errorCode?: 'INVALID_KEY' | 'ALREADY_ACTIVATED' | 'NOT_ASSIGNED' | 'NETWORK_ERROR';
}

export interface LicenseActivationResult {
  success: boolean;
  error?: string;
  errorCode?: 'NOT_AUTHENTICATED' | 'INVALID_KEY' | 'ALREADY_ACTIVATED' | 'ACTIVATION_FAILED';
}

/**
 * Validate a license key against the database
 * Returns customer info if valid for pre-filling signup form
 */
export async function validateLicense(licenseKey: string): Promise<LicenseValidationResult> {
  try {
    const { data, error } = await supabase.functions.invoke('validate-license', {
      body: { licenseKey }
    });

    if (error) {
      console.error('[licenseService] Edge function error:', error);
      return {
        valid: false,
        error: 'Unable to verify your license. Please check your connection and try again.',
        errorCode: 'NETWORK_ERROR'
      };
    }

    return data as LicenseValidationResult;
  } catch (err) {
    console.error('[licenseService] Unexpected error:', err);
    return {
      valid: false,
      error: 'Unable to verify your license. Please check your connection and try again.',
      errorCode: 'NETWORK_ERROR'
    };
  }
}

/**
 * Activate a license after successful account creation
 * Must be called with an authenticated user
 */
export async function activateLicense(licenseKey: string): Promise<LicenseActivationResult> {
  try {
    const { data, error } = await supabase.functions.invoke('activate-license', {
      body: { licenseKey }
    });

    if (error) {
      console.error('[licenseService] Activation error:', error);
      return {
        success: false,
        error: 'Failed to activate license. Please contact support.',
        errorCode: 'ACTIVATION_FAILED'
      };
    }

    return data as LicenseActivationResult;
  } catch (err) {
    console.error('[licenseService] Unexpected activation error:', err);
    return {
      success: false,
      error: 'Failed to activate license. Please contact support.',
      errorCode: 'ACTIVATION_FAILED'
    };
  }
}

/**
 * Save validated license data to localStorage for use during signup
 */
export function saveLicenseToStorage(data: LicenseValidationResult): void {
  try {
    localStorage.setItem(LICENSE_STORAGE_KEY, JSON.stringify(data));
  } catch (err) {
    console.error('[licenseService] Failed to save license to storage:', err);
  }
}

/**
 * Get license data from localStorage
 */
export function getLicenseFromStorage(): LicenseValidationResult | null {
  try {
    const stored = localStorage.getItem(LICENSE_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as LicenseValidationResult;
  } catch (err) {
    console.error('[licenseService] Failed to read license from storage:', err);
    return null;
  }
}

/**
 * Clear license data from localStorage (after successful activation)
 */
export function clearLicenseFromStorage(): void {
  try {
    localStorage.removeItem(LICENSE_STORAGE_KEY);
  } catch (err) {
    console.error('[licenseService] Failed to clear license from storage:', err);
  }
}

/**
 * Check if there's a valid license in storage
 */
export function hasValidLicenseInStorage(): boolean {
  const license = getLicenseFromStorage();
  return license !== null && license.valid === true;
}

/**
 * Extract first name from full customer name
 */
export function extractFirstName(fullName: string | undefined): string {
  if (!fullName) return '';
  const parts = fullName.trim().split(/\s+/);
  return parts[0] || '';
}

/**
 * Format a license key for display (uppercase, with dashes)
 */
export function formatLicenseKey(key: string): string {
  return key.trim().toUpperCase();
}

/**
 * Get user-friendly error message for license errors
 */
export function getLicenseErrorMessage(errorCode: string | undefined): string {
  switch (errorCode) {
    case 'INVALID_KEY':
      return "This license key wasn't recognized. Please check for typos.";
    case 'ALREADY_ACTIVATED':
      return "This license has already been used. Contact support if you need help.";
    case 'NOT_ASSIGNED':
      return "This license hasn't been processed yet. Please wait a few minutes and try again.";
    case 'NETWORK_ERROR':
      return "Unable to verify your license. Please check your connection and try again.";
    case 'ACTIVATION_FAILED':
      return "Your account was created, but we couldn't link your license. Please contact support.";
    default:
      return "An unexpected error occurred. Please try again.";
  }
}
